import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Edit2,
  Trash2,
  Eye,
  EyeOff,
  Search,
  Filter,
  ChevronDown,
  ThumbsUp,
  ThumbsDown,
  BarChart2,
  X,
  Save,
  HelpCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';
import { LocaleSelector } from '@/components/LocaleSelector';
import { useAdminLocale } from '@/hooks/useAdminLocale';

const supabase = getSupabaseAdminClient();

interface Guide {
  id: string;
  title: string;
  slug: string;
}

interface GuideCategory {
  id: string;
  guide_id: string;
  name: string;
  slug: string;
}

interface GuideFAQ {
  id: string;
  guide_id: string;
  category_id: string | null;
  question: string;
  short_answer: string;
  long_answer: string | null;
  keywords: string[] | null;
  is_active: boolean;
  view_count: number;
  helpful_count: number;
  not_helpful_count: number;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export function GuideFAQsPage() {
  const queryClient = useQueryClient();
  const { contentLocale } = useAdminLocale();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGuide, setSelectedGuide] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [isCreating, setIsCreating] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState<GuideFAQ | null>(null);

  // Fetch guides
  const { data: guides } = useQuery({
    queryKey: ['admin-guides', contentLocale],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('guides')
        .select('id, title, slug')
        .eq('locale', contentLocale)
        .order('order_index');
      if (error) throw error;
      return data as Guide[];
    },
  });

  // Fetch categories
  const { data: categories } = useQuery({
    queryKey: ['admin-guide-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('guide_categories')
        .select('id, guide_id, name, slug')
        .order('order_index');
      if (error) throw error;
      return data as GuideCategory[];
    },
  });

  // Fetch FAQs
  const { data: faqs, isLoading } = useQuery({
    queryKey: ['admin-guide-faqs', selectedGuide, selectedCategory, contentLocale],
    queryFn: async () => {
      let query = supabase.from('guide_faqs').select('*').eq('locale', contentLocale).order('sort_order');

      if (selectedGuide) {
        query = query.eq('guide_id', selectedGuide);
      }
      if (selectedCategory) {
        query = query.eq('category_id', selectedCategory);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as GuideFAQ[];
    },
  });

  // Create FAQ
  const createFAQ = useMutation({
    mutationFn: async (data: Partial<GuideFAQ>) => {
      // Get max sort_order for this guide
      const existingFAQs = faqs?.filter((f) => f.guide_id === data.guide_id) || [];
      const maxOrder = existingFAQs.reduce((max, f) => Math.max(max, f.sort_order), 0);

      const { error } = await (supabase.from('guide_faqs') as any).insert({
        guide_id: data.guide_id,
        category_id: data.category_id || null,
        question: data.question,
        short_answer: data.short_answer,
        long_answer: data.long_answer || null,
        keywords: data.keywords || [],
        is_active: true,
        sort_order: maxOrder + 1,
        locale: contentLocale,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-guide-faqs'] });
      setIsCreating(false);
      toast.success('Frage erstellt');
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });

  // Update FAQ
  const updateFAQ = useMutation({
    mutationFn: async (data: GuideFAQ) => {
      const { error } = await (supabase.from('guide_faqs') as any)
        .update({
          guide_id: data.guide_id,
          category_id: data.category_id || null,
          question: data.question,
          short_answer: data.short_answer,
          long_answer: data.long_answer || null,
          keywords: data.keywords || [],
          is_active: data.is_active,
          locale: contentLocale,
        })
        .eq('id', data.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-guide-faqs'] });
      setEditingFAQ(null);
      toast.success('Frage aktualisiert');
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });

  // Delete FAQ
  const deleteFAQ = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('guide_faqs').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-guide-faqs'] });
      toast.success('Frage gelöscht');
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });

  // Toggle active
  const toggleActive = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await (supabase.from('guide_faqs') as any).update({ is_active }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-guide-faqs'] });
    },
  });

  // Filter FAQs
  const filteredFAQs = faqs?.filter((faq) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      faq.question.toLowerCase().includes(query) ||
      faq.short_answer.toLowerCase().includes(query) ||
      faq.long_answer?.toLowerCase().includes(query) ||
      faq.keywords?.some((k) => k.toLowerCase().includes(query))
    );
  });

  const getGuideName = (guideId: string) => {
    return guides?.find((g) => g.id === guideId)?.title || '-';
  };

  const getCategoryName = (categoryId: string | null) => {
    if (!categoryId) return null;
    return categories?.find((c) => c.id === categoryId)?.name;
  };

  const availableCategories = categories?.filter((c) => c.guide_id === selectedGuide) || [];

  // Stats
  const totalViews = faqs?.reduce((sum, f) => sum + f.view_count, 0) || 0;
  const totalHelpful = faqs?.reduce((sum, f) => sum + f.helpful_count, 0) || 0;
  const totalNotHelpful = faqs?.reduce((sum, f) => sum + f.not_helpful_count, 0) || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Häufige Fragen (FAQ)</h1>
            <p className="text-slate-500 mt-1">Verwalte Fragen und Antworten zu Behördenthemen</p>
          </div>
          <LocaleSelector />
          <button
            onClick={() => setIsCreating(true)}
            className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg
                       hover:bg-teal-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Neue Frage
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-blue-500" />
              <span className="text-2xl font-bold text-slate-900">{faqs?.length || 0}</span>
            </div>
            <div className="text-sm text-slate-500">Gesamt Fragen</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <BarChart2 className="w-5 h-5 text-blue-500" />
              <span className="text-2xl font-bold text-slate-900">{totalViews}</span>
            </div>
            <div className="text-sm text-slate-500">Aufrufe</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <ThumbsUp className="w-5 h-5 text-green-500" />
              <span className="text-2xl font-bold text-slate-900">{totalHelpful}</span>
            </div>
            <div className="text-sm text-slate-500">Hilfreich</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <ThumbsDown className="w-5 h-5 text-red-500" />
              <span className="text-2xl font-bold text-slate-900">{totalNotHelpful}</span>
            </div>
            <div className="text-sm text-slate-500">Nicht hilfreich</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Suche nach Frage, Antwort, Keywords..."
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg
                         focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <select
              value={selectedGuide}
              onChange={(e) => {
                setSelectedGuide(e.target.value);
                setSelectedCategory('');
              }}
              className="pl-10 pr-8 py-2 border border-slate-200 rounded-lg appearance-none
                         focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            >
              <option value="">Alle Guides</option>
              {guides?.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.title}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
          {selectedGuide && availableCategories.length > 0 && (
            <div className="relative">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 pr-8 py-2 border border-slate-200 rounded-lg appearance-none
                           focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              >
                <option value="">Alle Kategorien</option>
                {availableCategories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
          )}
        </div>
      </div>

      {/* Create Form */}
      {isCreating && (
        <FAQForm
          guides={guides || []}
          categories={categories || []}
          onSubmit={(data) => createFAQ.mutate(data)}
          onCancel={() => setIsCreating(false)}
          isLoading={createFAQ.isPending}
        />
      )}

      {/* Edit Form */}
      {editingFAQ && (
        <FAQForm
          faq={editingFAQ}
          guides={guides || []}
          categories={categories || []}
          onSubmit={(data) => updateFAQ.mutate({ ...editingFAQ, ...data })}
          onCancel={() => setEditingFAQ(null)}
          isLoading={updateFAQ.isPending}
          isEditing
        />
      )}

      {/* FAQs List */}
      <div className="bg-white rounded-lg shadow-sm divide-y divide-slate-100">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin h-8 w-8 border-2 border-teal-500 border-t-transparent rounded-full mx-auto" />
          </div>
        ) : filteredFAQs?.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            {searchQuery ? 'Keine Ergebnisse gefunden' : 'Noch keine Fragen vorhanden'}
          </div>
        ) : (
          filteredFAQs?.map((faq) => (
            <div key={faq.id} className="p-4 hover:bg-slate-50">
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <HelpCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    <span className={`font-medium ${!faq.is_active && 'text-slate-400'}`}>
                      {faq.question}
                    </span>
                    {!faq.is_active && (
                      <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">
                        Inaktiv
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 line-clamp-2 mb-2 ml-6">{faq.short_answer}</p>
                  <div className="flex items-center gap-4 text-xs text-slate-400 ml-6">
                    <span className="bg-slate-100 px-2 py-0.5 rounded">{getGuideName(faq.guide_id)}</span>
                    {getCategoryName(faq.category_id) && (
                      <span className="bg-slate-100 px-2 py-0.5 rounded">
                        {getCategoryName(faq.category_id)}
                      </span>
                    )}
                    {faq.long_answer && (
                      <span className="text-blue-500">+ Ausführliche Antwort</span>
                    )}
                    {faq.keywords && faq.keywords.length > 0 && (
                      <span className="text-slate-400">
                        Keywords: {faq.keywords.slice(0, 3).join(', ')}
                        {faq.keywords.length > 3 && ` +${faq.keywords.length - 3}`}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2">
                  {/* Stats */}
                  <div className="flex items-center gap-3 text-xs text-slate-400">
                    <span className="flex items-center gap-1">
                      <BarChart2 className="w-3.5 h-3.5" />
                      {faq.view_count}
                    </span>
                    <span className="flex items-center gap-1 text-green-600">
                      <ThumbsUp className="w-3.5 h-3.5" />
                      {faq.helpful_count}
                    </span>
                    <span className="flex items-center gap-1 text-red-500">
                      <ThumbsDown className="w-3.5 h-3.5" />
                      {faq.not_helpful_count}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() =>
                        toggleActive.mutate({
                          id: faq.id,
                          is_active: !faq.is_active,
                        })
                      }
                      className={`p-1.5 rounded transition-colors ${
                        faq.is_active
                          ? 'text-green-600 hover:bg-green-50'
                          : 'text-slate-400 hover:bg-slate-100'
                      }`}
                      title={faq.is_active ? 'Deaktivieren' : 'Aktivieren'}
                    >
                      {faq.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => setEditingFAQ(faq)}
                      className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        if (confirm('Diese Frage wirklich löschen?')) {
                          deleteFAQ.mutate(faq.id);
                        }
                      }}
                      className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// FAQ Form Component
interface FAQFormProps {
  faq?: GuideFAQ;
  guides: Guide[];
  categories: GuideCategory[];
  onSubmit: (data: Partial<GuideFAQ>) => void;
  onCancel: () => void;
  isLoading: boolean;
  isEditing?: boolean;
}

function FAQForm({ faq, guides, categories, onSubmit, onCancel, isLoading, isEditing }: FAQFormProps) {
  const [guideId, setGuideId] = useState(faq?.guide_id || '');
  const [categoryId, setCategoryId] = useState(faq?.category_id || '');
  const [question, setQuestion] = useState(faq?.question || '');
  const [shortAnswer, setShortAnswer] = useState(faq?.short_answer || '');
  const [longAnswer, setLongAnswer] = useState(faq?.long_answer || '');
  const [keywordsInput, setKeywordsInput] = useState(faq?.keywords?.join(', ') || '');

  const availableCategories = categories.filter((c) => c.guide_id === guideId);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const keywords = keywordsInput
      .split(',')
      .map((k) => k.trim().toLowerCase())
      .filter((k) => k.length > 0);

    onSubmit({
      guide_id: guideId,
      category_id: categoryId || null,
      question,
      short_answer: shortAnswer,
      long_answer: longAnswer || null,
      keywords,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-900">
          {isEditing ? 'Frage bearbeiten' : 'Neue Frage erstellen'}
        </h2>
        <button type="button" onClick={onCancel} className="p-2 text-slate-400 hover:text-slate-600">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Guide *</label>
            <select
              value={guideId}
              onChange={(e) => {
                setGuideId(e.target.value);
                setCategoryId('');
              }}
              required
              className="w-full px-3 py-2 border border-slate-200 rounded-lg
                         focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            >
              <option value="">Guide wählen...</option>
              {guides.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.title}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Kategorie (optional)</label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              disabled={!guideId || availableCategories.length === 0}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg
                         focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500
                         disabled:bg-slate-50 disabled:text-slate-400"
            >
              <option value="">Keine Kategorie</option>
              {availableCategories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Frage *</label>
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            required
            placeholder="z.B. Wie lange bekomme ich Elterngeld?"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg
                       focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Kurze Antwort *</label>
          <textarea
            value={shortAnswer}
            onChange={(e) => setShortAnswer(e.target.value)}
            required
            rows={3}
            placeholder="Kompakte Zusammenfassung der Antwort..."
            className="w-full px-3 py-2 border border-slate-200 rounded-lg resize-none
                       focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
          />
          <p className="text-xs text-slate-400 mt-1">
            Wird direkt im Modal angezeigt. Sollte kurz und prägnant sein.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Ausführliche Antwort (optional)
          </label>
          <textarea
            value={longAnswer}
            onChange={(e) => setLongAnswer(e.target.value)}
            rows={5}
            placeholder="Detaillierte Erklärung mit mehr Kontext..."
            className="w-full px-3 py-2 border border-slate-200 rounded-lg resize-none
                       focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
          />
          <p className="text-xs text-slate-400 mt-1">
            Zusätzliche Details für Nutzer, die mehr wissen möchten.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Keywords (kommagetrennt)</label>
          <input
            type="text"
            value={keywordsInput}
            onChange={(e) => setKeywordsInput(e.target.value)}
            placeholder="dauer, monate, zeitraum"
            className="w-full px-3 py-2 border border-slate-200 rounded-lg
                       focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
          />
          <p className="text-xs text-slate-400 mt-1">Verbessert die Suche.</p>
        </div>
      </div>

      <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-100">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
        >
          Abbrechen
        </button>
        <button
          type="submit"
          disabled={isLoading || !guideId || !question || !shortAnswer}
          className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg
                     hover:bg-teal-600 disabled:opacity-50 transition-colors"
        >
          <Save className="w-4 h-4" />
          {isEditing ? 'Speichern' : 'Erstellen'}
        </button>
      </div>
    </form>
  );
}
